/**
 * Purpose: {@link ConversationDriver} — wraps @ada/llm-client's
 *          `text.generateWithTools` with copilot-flavoured concerns:
 *          system-prompt assembly, app-id propagation, and integration
 *          with the {@link ClientToolRegistry}. The widget calls
 *          `driver.send(messages)` once per user turn and gets back the
 *          assistant's final text.
 *
 * Implementation notes:
 *   - Re-uses the agent loop already implemented in `runToolLoop`
 *     (packages/llm-client/src/client.ts:422). No new agent loop here.
 *   - Multi-turn conversation history is rebuilt into the `prompt`
 *     parameter on each call: alternating user/assistant entries. This
 *     is the simplest representation that works with the current
 *     `text.generateWithTools` shape (which does not yet support an
 *     externally-supplied conversation array, only the opaque blob it
 *     manages internally per call). When P3 lands proper multi-turn
 *     persistence we revisit this — for P0 each `send()` is a fresh,
 *     fully-replayed conversation. Cost is fine because Anthropic prompt
 *     caching de-duplicates the prefix.
 *   - The driver does not retry, debounce, or rate-limit — those are the
 *     widget's UI concerns (a single in-flight send, disable input).
 *   - System prompt is assembled once per `send()` from `systemHints`,
 *     `appId`, and a tiny standing preamble. Knowledge / memory recall
 *     enters as server-tools in P1 / P3, not as inlined system text.
 *
 * Created: 2026-05-08 17:00
 *
 * Changelog:
 *   - 2026-05-08 17:00 — Initial version. P0 surface: send(messages),
 *                        no tool registry usage yet (registry passed in
 *                        for forward-compat but unused — `text.generate`
 *                        with empty tools is just a one-shot completion).
 *   - 2026-05-09 15:45 — P1: forward `serverTools` (a list of
 *                        server-resolved tool names) through to
 *                        `text.generateWithTools`. The agent loop now
 *                        actually has tools to call when the host opts in.
 *   - 2026-05-09 16:30 — Reword STANDING_PREAMBLE: "embedded in an Ada
 *                        application" instead of "Ada SPA". The driver
 *                        is host-agnostic (widget, CLI, future hosts);
 *                        host-specific tone goes through `systemHints`.
 *   - 2026-05-09 19:00 — Slice 2 of P5 streaming: add `stream()`, a
 *     sibling of `send()` that returns an `AsyncIterable<StreamWithToolsEvent>`
 *     for hosts that want chunk-by-chunk rendering (CLI / widget). Same
 *     prompt + system + tool wiring as `send()`; the underlying transport
 *     is `llm.text.streamWithTools` over SSE.
 *   - 2026-05-14 09:30 — Switch `DEFAULT_MODEL` from `'anthropic-model-mid'`
 *     (which does NOT exist in the deployed LLM catalog) to
 *     `'anthropic-text-mid'` (the catalog's Sonnet-class alias,
 *     resolves to claude-sonnet-4-6). The old default broke every
 *     widget host that didn't pass an explicit `model` prop —
 *     credits-admin, knowledge-admin, api-keys-admin, trace-admin —
 *     with `[LLMModelMap] Unknown key: "anthropic-model-mid"`. The
 *     canonical alias list is the dispatcher's `llm.model.list`
 *     output; the legacy `anthropic-model-*` naming lives in
 *     packages/llm/src/seed-catalog.ts but was never synced to dev DDB.
 *   - 2026-05-16 10:30 — Multimodal prompt support. `#renderPrompt` now
 *     returns `PromptInput`: the historical flat-string format when no
 *     turn in the history carries `attachments`, or a structured
 *     `LLMMessage[]` when at least one turn does (so the LLM receives
 *     proper `image`/`file` parts alongside the text). The string path
 *     is unchanged for back-compat and prompt caching; the multimodal
 *     path drops the fake "User:/Assistant:" role markers in favour of
 *     real role tags, which is also more correct for current providers.
 *     Attachments are gated by the widget on the active model's
 *     `capabilities.fileInput`/`imageInput`, so the driver does not
 *     re-check capabilities here.
 *   - 2026-09-25 07:58 — `send()` / `stream()` accept an optional
 *     `{ signal }` (forwarded to the LLM transport) so the widget can offer
 *     a Stop button; add an optional `locale` option and a standing rule to
 *     reply in the user's language, falling back to the interface locale
 *     when the input language is unclear (Celestina feedback: Spanish UI,
 *     gibberish input should not flip the reply to English).
 *   - 2026-10-06 16:02 — Move the system-prompt assembly into the shared
 *     `system-prompt.ts` (`assembleCopilotSystemPrompt`) so the new in-chat
 *     realtime voice mode uses identical instructions. Output unchanged.
 */
import { assembleCopilotSystemPrompt } from './system-prompt.js';
const DEFAULT_MODEL = 'anthropic-text-mid';
const DEFAULT_MAX_ROUNDS = 10;
const DEFAULT_TOOL_TIMEOUT_MS = 30_000;
export class ConversationDriver {
    #opts;
    constructor(opts) {
        this.#opts = opts;
    }
    /** App id this driver is bound to. */
    get appId() {
        return this.#opts.appId;
    }
    /**
     * Execute one user turn. The full message history is replayed each
     * call (see Implementation notes); the caller is expected to append
     * the user's new turn before calling. Returns the assistant's reply
     * which the caller appends to its own state.
     */
    async send(history, callOpts = {}) {
        if (history.length === 0 || history[history.length - 1]?.role !== 'user') {
            throw new Error('ConversationDriver.send: history must end with a user message');
        }
        const prompt = this.#renderPrompt(history);
        const serverTools = (this.#opts.serverTools ?? []).map((name) => ({
            kind: 'server',
            name,
        }));
        const result = await this.#opts.llm.text.generateWithTools({
            model: this.#opts.model ?? DEFAULT_MODEL,
            systemPrompt: this.#assembleSystemPrompt(),
            prompt,
            maxRounds: this.#opts.maxRounds ?? DEFAULT_MAX_ROUNDS,
            toolCallTimeoutMs: this.#opts.toolCallTimeoutMs ?? DEFAULT_TOOL_TIMEOUT_MS,
            // P0: the registry is empty by design; the agent loop falls
            // through to a single `llm.text.generate` call. P2 wires this up.
            clientTools: this.#opts.toolRegistry.list().map((t) => ({
                name: t.name,
                description: t.description,
                parameters: t.parameters,
                execute: t.execute,
            })),
            // P1: server-resolved tools (e.g. docs_get, credits_usage_get).
            // The LLM service resolves each name from its LLMToolLoader registry
            // and runs the tool in-process; only the name + result cross the wire.
            serverTools,
        }, callOpts.signal ? { signal: callOpts.signal } : {});
        return {
            text: result.text,
            model: result.model,
            rounds: result.rounds,
        };
    }
    /**
     * Streaming variant of {@link send}. Returns an async iterable of
     * {@link StreamWithToolsEvent}s so hosts can render text chunks as
     * they arrive and surface tool-call status between rounds. Single
     * in-flight stream per call — the host should disable input until
     * the iterator finishes (a `final` event is yielded last).
     */
    async *stream(history, callOpts = {}) {
        if (history.length === 0 || history[history.length - 1]?.role !== 'user') {
            throw new Error('ConversationDriver.stream: history must end with a user message');
        }
        const prompt = this.#renderPrompt(history);
        const serverTools = (this.#opts.serverTools ?? []).map((name) => ({
            kind: 'server',
            name,
        }));
        const events = this.#opts.llm.text.streamWithTools({
            model: this.#opts.model ?? DEFAULT_MODEL,
            systemPrompt: this.#assembleSystemPrompt(),
            prompt,
            maxRounds: this.#opts.maxRounds ?? DEFAULT_MAX_ROUNDS,
            toolCallTimeoutMs: this.#opts.toolCallTimeoutMs ?? DEFAULT_TOOL_TIMEOUT_MS,
            clientTools: this.#opts.toolRegistry.list().map((t) => ({
                name: t.name,
                description: t.description,
                parameters: t.parameters,
                execute: t.execute,
            })),
            serverTools,
        }, callOpts.signal ? { signal: callOpts.signal } : {});
        yield* events;
    }
    #assembleSystemPrompt() {
        return assembleCopilotSystemPrompt({
            appId: this.#opts.appId,
            ...(this.#opts.locale ? { locale: this.#opts.locale } : {}),
            ...(this.#opts.systemHints ? { systemHints: this.#opts.systemHints } : {}),
        });
    }
    /**
     * Renders the conversation history into a {@link PromptInput} the LLM
     * service accepts. Two output shapes:
     *
     *   - **No attachments anywhere in the history** → returns a flat
     *     string. First turn: just `content`. Multi-turn: a single string
     *     with synthetic `User:` / `Assistant:` role markers followed by
     *     a trailing `Assistant:` cue. This is the historical wire format;
     *     keeping it preserves Anthropic prompt-cache hit-rate on the
     *     stable prefix for hosts that have never sent a file.
     *
     *   - **At least one user turn has attachments** → returns a proper
     *     `LLMMessage[]`. User turns with attachments become
     *     `{ role: 'user', content: [TextPart, ...filePart] }`; everything
     *     else becomes a string-content message with real role tags
     *     (`'user'` / `'assistant'`). The synthetic role markers are gone
     *     in this path — providers see the conversation in its canonical
     *     multi-turn shape, which is also what `LLMPrompt.toAISDKMessages`
     *     expects.
     *
     * Image-vs-file routing is driven by `mimeType.startsWith('image/')`:
     * an `image/*` attachment lands as `LLMImagePart`, anything else as
     * `LLMFilePart`. Both consume the same `data:` URI in `attachment.data`.
     */
    #renderPrompt(history) {
        const hasAttachments = history.some((m) => m.attachments !== undefined && m.attachments.length > 0);
        if (!hasAttachments) {
            if (history.length === 1) {
                // First turn: just the user content, no role markers.
                return history[0].content;
            }
            const lines = [];
            for (const msg of history) {
                lines.push(`${msg.role === 'user' ? 'User' : 'Assistant'}: ${msg.content}`);
            }
            lines.push('Assistant:');
            return lines.join('\n\n');
        }
        return history.map((msg) => {
            if (msg.role === 'assistant') {
                return { role: 'assistant', content: msg.content };
            }
            if (msg.attachments && msg.attachments.length > 0) {
                const parts = [];
                // Text first so providers anchor the question before the files.
                // An empty `content` (file-only message) is allowed — we omit
                // the text part entirely so we don't ship an empty string,
                // which `TextPartSchema` rejects.
                if (msg.content.length > 0) {
                    parts.push({ type: 'text', text: msg.content });
                }
                for (const att of msg.attachments) {
                    parts.push(attachmentToPromptPart(att));
                }
                return { role: 'user', content: parts };
            }
            return { role: 'user', content: msg.content };
        });
    }
}
/**
 * Convert a {@link CopilotAttachment} into the wire-shape part the LLM
 * service expects. `image/*` MIME types route to `LLMImagePart` (the
 * preferred path for vision models — providers accept it more readily);
 * everything else rides as `LLMFilePart`. Both carry the bytes inline
 * as a `data:` URI taken straight from the attachment.
 */
function attachmentToPromptPart(att) {
    if (att.mimeType.startsWith('image/')) {
        return { type: 'image', image: att.data, mimeType: att.mimeType };
    }
    return { type: 'file', data: att.data, mimeType: att.mimeType };
}
//# sourceMappingURL=conversation-driver.js.map